import { useEffect, useMemo } from "react";
import { HeatMixDetailsTab } from "./mixDetails/HeatMixDetailsTab";
import { HeatDetailsChemistryTab } from "./chemistryTab/HeatDetailsChemistryTab";
import { useTenantTranslation } from "hooks/formatters";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Skeleton, Stack, Tooltip } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { DrawerLayout } from "components/common/panels/layout";
import { TitledTabs } from "components/common/titledTabs";

import { HeatTimelineTab } from "./timelineTab/HeatTimelineTab";
import { HeatLayeringComplianceTab } from "./layeringTab/HeatLayeringComplianceTab";
import { equalSets } from "./utils";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";
import { useHeatsSelection } from "components/strategyDashboard/heats/context/HeatsSelectionContext";
import { useHeatsFilter } from "components/strategyDashboard/heats/context/HeatsFilterContext";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useAdjacentHeatHotkeys } from "hooks/hotkeys";

interface HeatDetailsDrawerProps {
  highlightedMaterial?: string;
}

export default function HeatDetailsDrawer({
  highlightedMaterial,
}: HeatDetailsDrawerProps = {}) {
  const { t } = useTenantTranslation();
  const { t: tGeneral } = useTranslation();
  const { hasLifts, hasBaskets, materials, data, hasPriceComparison } =
    useHeatsData();
  const {
    selectedHeat: heatData,
    selectedHeatId,
    setSelectedHeatId,
    selectedTabKey,
    setSelectedTabKey,
    gridApiRef,
  } = useHeatsSelection();
  const { ignoredMaterials } = useHeatsFilter();
  const showLayering = useIsBehindFeatureFlag("bucket_layering_compliance");
  const availableMixSources = data?.available_mix_sources ?? [];
  const mixedMaterialPairs = heatData?.mixed_material_pairs ?? [];

  const { previousHeatId, nextHeatId } = useMemo(() => {
    const sortedRowIds = gridApiRef.current?.getSortedRowIds();
    const currentHeatIds =
      sortedRowIds && sortedRowIds.length > 0
        ? (sortedRowIds as string[])
        : (data?.series.map((heat) => heat.heat_id) ?? []);
    const currentHeatIndex = currentHeatIds.indexOf(selectedHeatId ?? "");
    return {
      previousHeatId:
        currentHeatIndex > 0 ? currentHeatIds[currentHeatIndex - 1] : undefined,
      nextHeatId:
        currentHeatIndex >= 0 && currentHeatIndex < currentHeatIds.length - 1
          ? currentHeatIds[currentHeatIndex + 1]
          : undefined,
    };
  }, [gridApiRef, data, selectedHeatId]);

  const goToPreviousHeat = previousHeatId
    ? () => {
        setSelectedHeatId(previousHeatId);
      }
    : undefined;
  const goToNextHeat = nextHeatId
    ? () => {
        setSelectedHeatId(nextHeatId);
      }
    : undefined;

  useAdjacentHeatHotkeys(goToPreviousHeat, goToNextHeat);

  // If the user had the chemistry tab open and switches to a heat with no
  // chemistry data, fall back to the first tab rather than showing an empty chart.
  useEffect(() => {
    if (selectedTabKey === "chemistry" && heatData?.samples.length === 0) {
      setSelectedTabKey("mixDetails");
    }
  }, [heatData, selectedTabKey, setSelectedTabKey]);

  const tabSpecs =
    heatData === null || materials === undefined
      ? []
      : [
          {
            title: t("mixDetails"),
            key: "mixDetails",
            content: (
              <HeatMixDetailsTab
                mixes={heatData.mixes.filter((mix) =>
                  equalSets(new Set(mix.ignored_materials), ignoredMaterials)
                )}
                mixedMaterialPairs={mixedMaterialPairs}
                availableMixSources={availableMixSources}
                ignoredMaterials={ignoredMaterials}
                hasLifts={hasLifts && !hasPriceComparison ? true : false}
                hasBaskets={hasBaskets}
                materials={materials}
                highlightedMaterial={highlightedMaterial}
              />
            ),
          },
          ...(showLayering
            ? [
                {
                  title: t("layering"),
                  key: "layering",
                  content: <HeatLayeringComplianceTab heatData={heatData} />,
                },
              ]
            : []),
          ...(hasLifts
            ? [
                {
                  title: t("timeline"),
                  key: "timeline",
                  content: (
                    <HeatTimelineTab
                      heatData={heatData}
                      materials={materials}
                    />
                  ),
                },
              ]
            : []),
          {
            title: t("chemistry"),
            key: "chemistry",
            content: <HeatDetailsChemistryTab heatData={heatData} />,
            disabled: heatData.samples.length === 0,
            disabledTooltip: tGeneral("noChemistryData"),
          },
        ];

  return (
    <DrawerLayout
      open={!!selectedHeatId}
      doClose={() => {
        setSelectedHeatId(null);
      }}
      title={
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          component="span"
          sx={{ display: "inline-flex" }}
        >
          <span>
            {t("detailsForHeat")} #{selectedHeatId ?? ""}
          </span>
          <Tooltip title={t("previousHeat")}>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                position: "relative",
                top: "1px",
                marginLeft: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={goToPreviousHeat}
                disabled={goToPreviousHeat === undefined}
                aria-label={t("previousHeat")}
                sx={{ padding: 0, width: 20, height: 20 }}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title={t("nextHeat")}>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                position: "relative",
                top: "1px",
              }}
            >
              <IconButton
                size="small"
                onClick={goToNextHeat}
                disabled={goToNextHeat === undefined}
                aria-label={t("nextHeat")}
                sx={{ padding: 0, width: 20, height: 20 }}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        </Stack>
      }
      width={Math.max(300 * availableMixSources.length - 100, 1200)}
    >
      {heatData && materials ? (
        <TitledTabs
          tabSpecs={tabSpecs}
          selectedTabKey={selectedTabKey}
          onChange={setSelectedTabKey}
        />
      ) : (
        <Skeleton height={300} />
      )}
    </DrawerLayout>
  );
}
